import { makeStyles, tokens, Skeleton, SkeletonItem, Text } from '@fluentui/react-components';

type CanvasSkeletonStateProps = {
  /** Status message to display during generation */
  statusMessage?: string | undefined;
  /** The user's initial prompt that triggered generation */
  userPrompt?: string | undefined;
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    gap: tokens.spacingVerticalXXL,
    padding: tokens.spacingHorizontalXXL,
  },
  promptContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalS,
    maxWidth: '600px',
    textAlign: 'center',
  },
  promptLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  promptText: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  statusContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalS,
  },
  statusText: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  // Horizontal workflow skeleton layout
  nodesContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  // Trigger skeleton
  triggerSkeleton: {
    width: '160px',
    height: '60px',
    borderRadius: tokens.borderRadiusMedium,
  },
  // Horizontal connector line between nodes
  connectorLine: {
    width: '40px',
    height: '2px',
    backgroundColor: tokens.colorNeutralStroke2,
  },
  // Action skeleton
  actionSkeleton: {
    width: '140px',
    height: '55px',
    borderRadius: tokens.borderRadiusMedium,
  },
});

/**
 * Displayed while the assistant is generating a workflow
 * Shows skeleton placeholders that mimic the shape of workflow nodes
 */
export const CanvasSkeletonState = ({
  statusMessage = 'Generating your workflow...',
  userPrompt,
}: CanvasSkeletonStateProps) => {
  const styles = useStyles();

  return (
    <div className={styles.container}>
      {/* User's prompt */}
      {userPrompt && (
        <div className={styles.promptContainer}>
          <Text className={styles.promptLabel}>Your request</Text>
          <Text className={styles.promptText}>{userPrompt}</Text>
        </div>
      )}

      {/* Status message */}
      <div className={styles.statusContainer}>
        <Text className={styles.statusText}>{statusMessage}</Text>
      </div>

      {/* Horizontal workflow skeleton */}
      <div className={styles.nodesContainer}>
        {/* Trigger skeleton */}
        <Skeleton>
          <SkeletonItem className={styles.triggerSkeleton} />
        </Skeleton>

        {/* Connector line */}
        <div className={styles.connectorLine} />

        {/* Action skeleton */}
        <Skeleton>
          <SkeletonItem className={styles.actionSkeleton} />
        </Skeleton>

        {/* Connector line */}
        <div className={styles.connectorLine} />

        {/* Action skeleton */}
        <Skeleton>
          <SkeletonItem className={styles.actionSkeleton} />
        </Skeleton>

        {/* Connector line */}
        <div className={styles.connectorLine} />

        {/* Action skeleton */}
        <Skeleton>
          <SkeletonItem className={styles.actionSkeleton} />
        </Skeleton>
      </div>
    </div>
  );
};

export default CanvasSkeletonState;
